import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { Navigate, useLocation } from "react-router";
import { Badge, Button, Card, ErrorText, Input, PageHeader, Spinner, Switch, TabbedLayout } from "../components/ui.tsx";
import { useBrand, useMe, useSettings } from "../hooks.ts";
import { useSaveSettings } from "./Settings.tsx";
import { McpServers } from "./Mcp.tsx";
import { SecretsSection } from "./Secrets.tsx";
import { BrowserPage } from "./Browser.tsx";

type Me = NonNullable<ReturnType<typeof useMe>["data"]>;
/** The live browser window: where the group gives the assistant a browser, and never while acting as someone. */
const mayBrowse = (me: Me) => !!me.group?.permissions.browser && !me.user.impersonatedBy;
/** Own MCP servers and secrets are for groups that may add servers or run commands (no group: the defaults allow both). */
const mayDevelop = (me: Me) => (me.group ? me.group.permissions.customMcp || me.group.permissions.terminal : true);

/** Connected apps: everything the assistant can reach, one tab each. */
export function ConnectionsLayout() {
  const me = useMe();
  if (!me.data) return null;
  return (
    <TabbedLayout
      tabs={[
        { to: "/connections/chat", label: "Chat apps" },
        { to: "/connections/apps", label: "Apps" },
        ...(mayBrowse(me.data) ? [{ to: "/connections/browser", label: "Browser" }] : []),
        ...(mayDevelop(me.data) ? [{ to: "/connections/developer", label: "Developer" }] : []),
      ]}
    />
  );
}

/** Chat apps come first; an app's or MCP server's sign-in coming back belongs to Apps. */
export function ConnectionsIndex() {
  const { search } = useLocation();
  const q = new URLSearchParams(search);
  return <Navigate to={["connected", "error", "mcp", "mcp_error"].some((k) => q.has(k)) ? `/connections/apps${search}` : "/connections/chat"} replace />;
}

export function BrowserTab() {
  const me = useMe();
  if (!me.data) return null;
  return mayBrowse(me.data) ? <BrowserPage /> : <Navigate to="/connections/chat" replace />;
}

export function DeveloperPage() {
  const me = useMe();
  const { search } = useLocation();
  if (!me.data) return null;
  if (!mayDevelop(me.data)) return <Navigate to={`/connections/apps${search}`} replace />;
  return (
    <>
      <PageHeader title="Developer" description="For technical work: MCP servers only your assistant uses, and API keys it can use in the commands it runs." />
      <McpServers part="own" />
      <SecretsSection />
    </>
  );
}

/** Adds a WhatsApp number by typing it: the bot sends a 6-digit code to it, typed back here. */
function WhatsAppNumber({ connected }: { connected: boolean }) {
  const qc = useQueryClient();
  const [phone, setPhone] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const send = useMutation({
    mutationFn: () => unwrap(api.me.messaging.whatsapp.number.$post({ json: { phone } })),
    onSuccess: (r) => {
      setSentTo(r.number);
      setCode("");
    },
  });
  const verify = useMutation({
    mutationFn: () => unwrap(api.me.messaging.whatsapp.verify.$post({ json: { code } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-messaging"] }),
  });
  if (!connected) return <p className="basis-full text-xs text-[var(--muted)]">WhatsApp is offline right now, so numbers can't be added. Try again later.</p>;
  return (
    <div className="basis-full space-y-2">
      {!sentTo ? (
        <form className="flex flex-wrap gap-2" onSubmit={(e) => (e.preventDefault(), send.mutate())}>
          <Input type="tel" inputMode="tel" autoComplete="tel" placeholder="+91 98765 43210" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-56 max-w-full" aria-label="Your WhatsApp number with country code" />
          <Button type="submit" disabled={!phone.trim() || send.isPending}>{send.isPending ? "Sending…" : "Send code"}</Button>
        </form>
      ) : (
        <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => (e.preventDefault(), verify.mutate())}>
          <span className="text-sm text-[var(--muted)]">Enter the code we sent on WhatsApp to <b className="text-[var(--text)]">{sentTo}</b>:</span>
          <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="w-28" aria-label="6-digit code" autoFocus />
          <Button type="submit" variant="primary" disabled={code.length !== 6 || verify.isPending}>{verify.isPending ? "Checking…" : "Link"}</Button>
          <button type="button" className="text-xs text-[var(--muted)] hover:text-[var(--text)]" onClick={() => (setSentTo(null), send.reset(), verify.reset())}>Change number</button>
        </form>
      )}
      <ErrorText error={send.error ?? verify.error} />
    </div>
  );
}

/** Reaching the assistant from Slack, Discord and WhatsApp: linking accounts, and how chats show its work. */
export function ChatAppsPage() {
  const qc = useQueryClient();
  const brand = useBrand();
  const settings = useSettings();
  const save = useSaveSettings();
  const chat = useQuery({ queryKey: ["my-messaging"], queryFn: () => unwrap(api.me.messaging.$get()) });
  const unlink = useMutation({
    mutationFn: (id: string) => unwrap(api.me.messaging[":id"].$delete({ param: { id } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-messaging"] }),
  });
  if (chat.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (chat.error) return <ErrorText error={chat.error} />;
  return (
    <>
      <PageHeader title="Chat apps" description="Talk to your assistant from the chat apps you already use. Your chats there stay between you and your assistant." />
      {!chat.data.available.length ? (
        <Card className="p-6 text-sm text-[var(--muted)]">Your admins haven't set up Slack, Discord or WhatsApp for your group yet. Until then, chat with your assistant here in the web app.</Card>
      ) : (
        <>
          <Card className="mb-8 divide-y divide-[var(--line)]">
            {chat.data.available.map((a) => {
              const linked = chat.data.identities.filter((i) => i.platform === a.platform);
              const name = { slack: "Slack", discord: "Discord", whatsapp: "WhatsApp" }[a.platform];
              return (
                <div key={a.platform} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5">
                  <div className="w-24 font-medium">{name}</div>
                  <div className="min-w-0 flex-1 text-sm text-[var(--muted)]">
                    {linked.length
                      ? linked.map((i) => (
                          <span key={i.id} className="mr-3 inline-flex items-center gap-2">
                            <Badge tone="good">linked</Badge> {i.displayName}
                            <button className="text-xs hover:text-red-700" onClick={() => confirm(`Disconnect ${name} account ${i.displayName}?`) && unlink.mutate(i.id)}>unlink</button>
                          </span>
                        ))
                      : a.platform === "slack"
                        ? <>Send a direct message to <b className="text-[var(--text)]">@{a.handle ?? brand.name}</b> — you're recognised by your Slack email.</>
                        : a.platform === "whatsapp"
                          ? <>Enter your WhatsApp number below, or message <b className="text-[var(--text)]">{a.handle ?? `the ${brand.name} bot`}</b> and send <b className="text-[var(--text)]">link</b>.</>
                          : <>Message <b className="text-[var(--text)]">{a.handle ?? `the ${brand.name} bot`}</b> and send <b className="text-[var(--text)]">link</b> to connect.</>}
                  </div>
                  {a.platform === "whatsapp" && !linked.length && <WhatsAppNumber connected={a.connected} />}
                </div>
              );
            })}
          </Card>
          <ErrorText error={unlink.error} />
          {settings.data && (
            <Card className="mb-8 px-5 py-3">
              <Switch
                label="Show live progress"
                hint="While your assistant works, the chat shows each step it takes (like “Searching the web — …”) instead of just “Working on it”. In a shared Slack channel, others in the thread see the steps too. You can also send “updates on” or “updates off” in the chat."
                checked={settings.data.toolUpdates}
                disabled={save.isPending}
                onChange={(v) => save.mutate({ toolUpdates: v })}
              />
              <ErrorText error={save.error} />
            </Card>
          )}
        </>
      )}
    </>
  );
}

type Toolkit = { slug: string; name: string; logo: string | null; description: string };

/** Apps the assistant works in on the person's behalf (Gmail, GitHub…), and the company's MCP tools. */
export function AppsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["connections"], queryFn: () => unwrap(api.me.connections.$get()) });
  const [search, setSearch] = useState("");
  const [justConnected] = useState(() => new URLSearchParams(location.search).get("connected"));
  useEffect(() => {
    if (justConnected) history.replaceState(null, "", location.pathname);
  }, [justConnected]);

  const found = useQuery({
    queryKey: ["toolkit-search", search],
    queryFn: () => unwrap(api.me.connections.toolkits.$get({ query: { q: search } })),
    enabled: !!q.data?.enabled && q.data.allToolkits,
  });
  const connect = useMutation({
    mutationFn: (toolkit: string) => unwrap(api.me.connections.connect.$post({ json: { toolkit } })),
    onSuccess: (r) => {
      location.href = r.redirectUrl;
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.me.connections[":id"].$delete({ param: { id } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["connections"] }),
  });

  const catalogue = useMemo<Toolkit[]>(() => {
    if (!q.data?.enabled) return [];
    const base = q.data.toolkits.filter((t) => t.allowed);
    if (!q.data.allToolkits) return base;
    const extra = (found.data?.toolkits ?? []).filter((t) => !base.some((b) => b.slug === t.slug));
    return [...base, ...extra];
  }, [q.data, found.data]);

  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const data = q.data;
  const finished = justConnected ? data.toolkits.find((t) => t.slug === justConnected) : undefined;

  return (
    <>
      <PageHeader title="Apps" description="Let your assistant work in apps like Gmail, GitHub or Google Drive on your behalf. Only you and your assistant can use your connections." />
      <McpServers part="company" />
      {!data.enabled ? (
        <Card className="p-6 text-sm text-[var(--muted)]">Connected apps aren't enabled for your group. Ask an admin if you need them.</Card>
      ) : (
        <>
          {justConnected && (
            <Card className="mb-4 border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800">
              Finished connecting {finished?.name ?? justConnected}. It can take a few seconds to show as active.
            </Card>
          )}
          <h2 className="mb-3 text-base font-semibold">Your connections</h2>
          <Card className="mb-8 divide-y divide-[var(--line)]">
            {data.accounts.length === 0 && <p className="px-5 py-8 text-center text-sm text-[var(--muted)]">Nothing connected yet.</p>}
            {data.accounts.map((a) => {
              const t = data.toolkits.find((x) => x.slug === a.toolkit);
              return (
                <div key={a.id} className="flex items-center gap-4 px-5 py-3.5">
                  <Logo t={t ?? { slug: a.toolkit, name: a.toolkit, logo: null, description: "" }} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{t?.name ?? a.toolkit}</div>
                    <div className="text-xs text-[var(--muted)]">{a.createdAt ? `Connected ${new Date(a.createdAt).toLocaleDateString()}` : ""}</div>
                  </div>
                  <Badge tone={a.status === "ACTIVE" ? "good" : a.status === "FAILED" || a.status === "EXPIRED" ? "bad" : "neutral"}>{a.status.toLowerCase()}</Badge>
                  <Button variant="ghost" className="h-8" disabled={remove.isPending} onClick={() => confirm(`Disconnect ${t?.name ?? a.toolkit}? Your assistant loses access immediately.`) && remove.mutate(a.id)}>
                    Disconnect
                  </Button>
                </div>
              );
            })}
          </Card>

          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold">Add a connection</h2>
            {data.allToolkits && <Input placeholder="Search 1,500+ apps" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />}
          </div>
          <ErrorText error={connect.error ?? remove.error} />
          <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {catalogue.map((t) => (
              <Card key={t.slug} className="flex items-start gap-3 p-4">
                <Logo t={t} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{t.name}</div>
                  <p className="line-clamp-2 text-xs text-[var(--muted)]">{t.description}</p>
                </div>
                <Button className="h-8 shrink-0" disabled={connect.isPending} onClick={() => connect.mutate(t.slug)}>
                  {connect.isPending && connect.variables === t.slug ? <Spinner /> : "Connect"}
                </Button>
              </Card>
            ))}
            {data.allToolkits && found.isFetching && <div className="grid place-items-center py-6"><Spinner /></div>}
          </div>
        </>
      )}
    </>
  );
}

function Logo({ t }: { t: Toolkit }) {
  return t.logo ? (
    <img src={t.logo} alt="" className="h-9 w-9 shrink-0 rounded-lg border border-[var(--line)] bg-white object-contain p-1" />
  ) : (
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--panel-2)] text-sm font-semibold uppercase">{t.name[0]}</div>
  );
}
